// ============================================================
// GitBasics.jsx
//
// A branded, responsive course content page for the
// "Git Basics: Core Commands" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Interactive command breakdown, staging area logic,
//           and history tracking essentials.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import PlayArrowIcon          from "@mui/icons-material/PlayArrow";
import AddCircleIcon          from "@mui/icons-material/AddCircle";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import SearchIcon             from "@mui/icons-material/Search";
import HistoryIcon            from "@mui/icons-material/History";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitBasics() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Essential Commands"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        The <span>Git Core Cycle</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Mastering the primary Git commands—init, add, commit, and status—is the first step toward professional version control. These tools allow you to track changes, stage progress, and build a reliable project history.
                    </Typography>
                </Box>

                {/* 1. INIT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PlayArrowIcon />} label="1. Initialize: git init" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Start tracking a project from scratch. This command creates a hidden <code>.git</code> folder in your directory, turning a normal folder into a Git Repository.
                    </Typography>
                    <CodeBlock
                        title="Repository Setup"
                        code={`git init\n# Result: Initialized empty Git repository in /path/to/project/.git/`}
                    />
                </Box>

                {/* 2. ADD */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AddCircleIcon />} label="2. Stage: git add" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Think of staging like a "loading dock." You choose which items (files) go onto the truck (the next commit).
                    </Typography>
                    <CodeBlock
                        title="Staging Area"
                        code={`# Stage a specific file\ngit add index.html\n\n# Stage all changes in the current directory\ngit add .`}
                    />
                </Box>

                {/* 3. COMMIT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CheckCircleIcon />} label="3. Snapshot: git commit" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        A commit is a permanent snapshot of your work. Every commit needs a meaningful message describing what changed.
                    </Typography>
                    <CodeBlock
                        title="Commit Logic"
                        code={`git commit -m "feat: implement user authentication logic"`}
                    />
                </Box>

                {/* 4. STATUS & LOG */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SearchIcon />} label="4. Inspect: git status & log" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Use these commands to see where you are and where you've been. <code>status</code> shows the current state, while <code>log</code> shows the timeline.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Current State"
                                code={`git status\n# Shows staged, unstaged, and untracked files`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Project History"
                                code={`git log --oneline\n# Shows a compact list of previous commits`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* WORKFLOW CHECKLIST */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Daily Workflow Checklist" />
                    <Grid container spacing={1.5}>
                        {[
                            "Run 'git status' before every 'git add' to avoid staging unwanted files.",
                            "Keep your commit messages concise but descriptive (imperative mood).",
                            "Stage small, related changes together rather than one giant commit.",
                            "Use 'git log' to verify your history before pushing to a remote.",
                            "Never commit sensitive data like API keys or passwords.",
                            "Check 'git status' after a commit to ensure a clean working tree."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Using <code>git add .</code> is convenient, but <code>git add -p</code> (patch mode) is more professional. It lets you review each change "chunk" individually before staging, ensuring total accuracy.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
